import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { bunkRequiredFor, bunkActiveFor } from '../utils/dormRelayoutConfig';
import { doc, setDoc, addDoc, collection, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { createCallable } from '../firebaseFunctions';
import {
  requireOwnerProjectId,
  withCreatedBy,
} from '../services/dorm/dormRoomOwnership';
import { Modal, Button } from './ui';
import {
  GOAL_DESCRIPTIONS,
  IN_TO_M,
} from '@shared-lib/dorm-optimizer-v2/constants';
// Canonical direction→facing-rotation conversion (local +Z along inward normal).
// Shared with the optimizer so the 3D box sits flush the same way placed items do.
import { facingRotation } from '@shared-lib/dorm-optimizer-v2/placement/helpers';
import { itemCorners, keepoutDisplayItems } from './dorm/builtInDisplay';
import { newUuid } from '../utils/newUuid';

const FT_TO_M = 0.3048;
const M_TO_PX = 100;
const PADDING = 30;
const WALL_THICKNESS = 0.15;
// Walls are drawn centered on the polygon line, so a box snapped to that line
// would overlap the wall band by half its thickness. Inset the box's back edge
// by half the wall thickness so it sits flush against the inner wall face.
const WALL_INNER_INSET = WALL_THICKNESS / 2;

// Default footprint for a marked built-in closet/cabinet (inches). w = width
// along the wall, d = depth into the room, h = height.
const DEFAULT_BUILTIN_INCHES = { w: 30, d: 24, h: 84 };
const BUILTIN_KEEPOUT_TYPE = 'dorm-fixed-keepout';
// Access zone reserved in front of a built-in (18"). Mirrors the optimizer's
// FIXED_OBSTACLE_CLEARANCE_M so the preview shows the space furniture is kept out of.
const BUILTIN_CLEARANCE_M = 18 * IN_TO_M;

// Fixed door/window layout for wizard dorms — mirrors the hardcoded dormConfig
// (doorWall:'south', windowWall:'north', windowCount:2) and the server's
// wallMap ({north:0, east:1, south:2, west:3}) so the Built-ins step can preview
// openings before the cloud function returns the real synthesized ones.
const DORM_DOOR_WALL_IDX = 2; // south
const DORM_WINDOW_WALL_IDX = 0; // north
const DORM_WINDOW_COUNT = 2;
const DORM_DOOR_WIDTH_M = 0.9;

const COLORS = {
  'dorm-bed': '#6ec6ff',
  'dorm-desk': '#4ecca3',
  'dorm-dresser': '#ffc857',
  'dorm-wardrobe': '#c084fc',
  'dorm-bookshelf': '#fb923c',
  'dorm-desk-chair': '#4ecca3',
  'dorm-wastebasket': '#94a3b8',
  'dorm-mini-fridge': '#38bdf8',
  'dorm-minifridge-compact': '#38bdf8',
  'dorm-minifridge-midsize': '#38bdf8',
  'dorm-shoe-rack': '#a78bfa',
  'dorm-laundry-hamper': '#f472b6',
  'dorm-rolling-cart': '#a78bfa',
  'dorm-beanbag': '#f472b6',
  'dorm-corkboard': '#d4a373',
  'dorm-whiteboard': '#e2e8f0',
  'dorm-poster': '#3b5ba5',
  'dorm-tapestry': '#8b5e83',
  'dorm-floating-shelf': '#cbb189',
  'dorm-command-hooks': '#94a3b8',
};

function getColor(typeKey) {
  if (!typeKey) return '#888';
  if (COLORS[typeKey]) return COLORS[typeKey];
  for (const [prefix, color] of Object.entries(COLORS)) {
    if (typeKey.startsWith(prefix)) return color;
  }
  return '#888';
}

const DORM_PROVIDED = [
  { key: 'dorm-desk-chair', label: 'Desk + Chair', perPerson: true },
  { key: 'dorm-dresser', label: 'Dresser', perPerson: true },
  { key: 'dorm-wardrobe', label: 'Wardrobe', perPerson: true },
  { key: 'dorm-bookshelf', label: 'Bookshelf', perPerson: true },
  {
    key: 'dorm-wastebasket',
    label: 'Wastebasket',
    perPerson: false,
    defaultQty: 1,
  },
  {
    key: 'dorm-corkboard',
    label: 'Corkboard',
    perPerson: true,
    defaultOff: true,
  },
  {
    key: 'dorm-whiteboard',
    label: 'Whiteboard',
    perPerson: true,
    defaultOff: true,
  },
  {
    key: 'dorm-poster',
    label: 'Poster',
    perPerson: true,
    defaultOff: true,
  },
  {
    key: 'dorm-tapestry',
    label: 'Tapestry',
    perPerson: true,
    defaultOff: true,
  },
  // Two items are deliberately NOT offered, for different reasons.
  //
  // 'dorm-mirror' (#2317) — its fault is downstream of the optimizer.
  //
  // 'dorm-floating-shelf' (#2318) — its PLACEMENT is correct and measured: it
  // places in every corpus scenario, wall-parented at its 54" mount height,
  // facing the room. So the "never places" half of that issue is ruled out.
  // But the RENDER has never been reproduced or refuted, so it stays out until
  // someone confirms it on a device — re-enabling it needs a visual check, not
  // another placement measurement.
  //
  // The fairy lights (#2315 / #2316) have never been offered by this wizard;
  // the iOS picker carries them.
  {
    key: 'dorm-command-hooks',
    label: 'Command Hooks',
    perPerson: true,
    defaultOff: true,
  },
];

const PERSONAL_ITEMS = [
  { key: 'dorm-minifridge-compact', label: 'Mini Fridge (Compact)' },
  { key: 'dorm-minifridge-midsize', label: 'Mini Fridge (Mid)' },
  { key: 'dorm-microwave', label: 'Microwave' },
  { key: 'dorm-stackable-drawers', label: 'Stackable Drawers' },
  { key: 'dorm-rolling-cart', label: 'Rolling Cart' },
  { key: 'dorm-shoe-rack', label: 'Shoe Rack' },
  { key: 'dorm-laundry-hamper', label: 'Laundry Hamper' },
  { key: 'dorm-tower-fan', label: 'Tower Fan' },
  { key: 'dorm-air-purifier-tower', label: 'Air Purifier' },
  { key: 'dorm-beanbag', label: 'Beanbag' },
  { key: 'dorm-floor-plant', label: 'Floor Plant' },
  { key: 'dorm-printer', label: 'Printer' },
  { key: 'dorm-stick-vacuum', label: 'Stick Vacuum' },
  { key: 'dorm-storage-bin', label: 'Storage Bin' },
  { key: 'dorm-surge-protector', label: 'Surge Protector' },
];

function buildInitialSelections(occupants) {
  const sel = {};
  for (const item of DORM_PROVIDED) {
    sel[item.key] = {
      enabled: !item.defaultOff,
      quantity: item.defaultOff
        ? 0
        : item.perPerson
          ? occupants
          : item.defaultQty || 1,
    };
  }
  for (const item of PERSONAL_ITEMS) {
    sel[item.key] = { enabled: false, quantity: 0 };
  }
  return sel;
}

// ---------------------------------------------------------------------------
// Room creation
// ---------------------------------------------------------------------------

function makeRoomWall(
  id,
  structureWallId,
  center,
  dimensions,
  rotation,
  corners
) {
  return {
    id,
    center,
    dimensions,
    // Derive the inward-facing rotation.y from the wall center instead of the
    // passed value. PolygonWalls.ensureInwardFacingCorners treats rotation.y as
    // the source of truth (inward normal = (sin ry, cos ry)); hand-coded values
    // here were wrong for some walls, so they culled backwards in dollhouse mode.
    // atan2(-center.x, -center.z) points the normal toward the room center
    // (origin) for every wall. See plan 2026-08-14 (dorm wall culling).
    rotation: { x: 0, y: Math.atan2(-center.x, -center.z), z: 0 },
    polygonCorners: corners.map((c) => ({ x: c[0], y: c[1], z: c[2] })),
    alignment: { structureWallId },
    story: 0,
    isUserCreated: true,
  };
}

function makeStructureWall(id, start, end, height) {
  const dx = end[0] - start[0];
  const dz = end[2] - start[2];
  const length = Math.hypot(dx, dz);
  const angle = Math.atan2(-dz, dx);
  return {
    id,
    center: {
      x: (start[0] + end[0]) / 2,
      y: height / 2,
      z: (start[2] + end[2]) / 2,
    },
    dimensions: { x: length, y: height, z: WALL_THICKNESS },
    rotation: { x: 0, y: angle, z: 0 },
    start: { x: start[0], y: start[1], z: start[2] },
    end: { x: end[0], y: end[1], z: end[2] },
    height,
    thickness: WALL_THICKNESS,
  };
}

function buildDormRoomData(widthFt, lengthFt) {
  const widthM = widthFt * FT_TO_M;
  const lengthM = lengthFt * FT_TO_M;
  const heightM = 8.5 * FT_TO_M;
  const halfW = widthM / 2;
  const halfL = lengthM / 2;

  const floorPolygon = [
    { x: -halfW, y: 0, z: -halfL },
    { x: halfW, y: 0, z: -halfL },
    { x: halfW, y: 0, z: halfL },
    { x: -halfW, y: 0, z: halfL },
  ];

  const roomWalls = [
    makeRoomWall(
      'rw-0',
      'sw-0',
      { x: 0, y: heightM / 2, z: -halfL },
      { x: widthM, y: heightM, z: WALL_THICKNESS },
      { x: 0, y: 0, z: 0 },
      [
        [-halfW, 0, -halfL],
        [halfW, 0, -halfL],
        [halfW, heightM, -halfL],
        [-halfW, heightM, -halfL],
      ]
    ),
    makeRoomWall(
      'rw-1',
      'sw-1',
      { x: halfW, y: heightM / 2, z: 0 },
      { x: lengthM, y: heightM, z: WALL_THICKNESS },
      { x: 0, y: Math.PI / 2, z: 0 },
      [
        [halfW, 0, -halfL],
        [halfW, 0, halfL],
        [halfW, heightM, halfL],
        [halfW, heightM, -halfL],
      ]
    ),
    makeRoomWall(
      'rw-2',
      'sw-2',
      { x: 0, y: heightM / 2, z: halfL },
      { x: widthM, y: heightM, z: WALL_THICKNESS },
      { x: 0, y: 0, z: 0 },
      [
        [halfW, 0, halfL],
        [-halfW, 0, halfL],
        [-halfW, heightM, halfL],
        [halfW, heightM, halfL],
      ]
    ),
    makeRoomWall(
      'rw-3',
      'sw-3',
      { x: -halfW, y: heightM / 2, z: 0 },
      { x: lengthM, y: heightM, z: WALL_THICKNESS },
      { x: 0, y: Math.PI / 2, z: 0 },
      [
        [-halfW, 0, halfL],
        [-halfW, 0, -halfL],
        [-halfW, heightM, -halfL],
        [-halfW, heightM, halfL],
      ]
    ),
  ];

  const structureWalls = [
    makeStructureWall('sw-0', [-halfW, 0, -halfL], [halfW, 0, -halfL], heightM),
    makeStructureWall('sw-1', [halfW, 0, -halfL], [halfW, 0, halfL], heightM),
    makeStructureWall('sw-2', [halfW, 0, halfL], [-halfW, 0, halfL], heightM),
    makeStructureWall('sw-3', [-halfW, 0, halfL], [-halfW, 0, -halfL], heightM),
  ];

  return {
    roomData: {
      name: 'Dorm Room',
      type: 'dorm', // user declaration — the retired `category` field is no longer written
      walls: roomWalls,
      floors: [
        {
          id: 'floor-0',
          center: { x: 0, y: 0, z: 0 },
          polygon: floorPolygon,
          polygonCorners: floorPolygon,
          dimensions: { x: widthM, y: 0, z: lengthM },
        },
      ],
      ceilings: [],
      doors: [],
      windows: [],
      openings: [],
      objects: [],
    },
    structureData: {
      name: 'Dorm Room',
      walls: structureWalls,
      floors: [{ id: 'floor-0', polygon: floorPolygon }],
      doors: [],
      windows: [],
      openings: [],
    },
  };
}

// ---------------------------------------------------------------------------
// SVG layout renderer
// ---------------------------------------------------------------------------

// Compute the world→SVG bounds/transform for a room render. Factored out so the
// marking step (StepMarkBuiltins) can invert clicks with EXACTLY the same
// minX/minZ/M_TO_PX/PADDING that LayoutSVG draws with — a drifting duplicate
// here would misplace every marked built-in.
function computeLayoutTransform({ walls, items, floorPolygon, wallThickness }) {
  const wt = wallThickness || 0;
  const allPoints = [];
  for (const w of walls || []) {
    if (w.start) allPoints.push(w.start);
    if (w.end) allPoints.push(w.end);
  }
  if (floorPolygon) allPoints.push(...floorPolygon);
  for (const item of items || []) allPoints.push(...itemCorners(item));

  const xs = allPoints.map((p) => p.x);
  const zs = allPoints.map((p) => p.z);
  const minX = Math.min(...xs) - wt;
  const maxX = Math.max(...xs) + wt;
  const minZ = Math.min(...zs) - wt;
  const maxZ = Math.max(...zs) + wt;

  return {
    minX,
    minZ,
    width: (maxX - minX) * M_TO_PX + PADDING * 2,
    height: (maxZ - minZ) * M_TO_PX + PADDING * 2,
    tx: (x) => (x - minX) * M_TO_PX + PADDING,
    tz: (z) => (z - minZ) * M_TO_PX + PADDING,
  };
}

function LayoutSVG({
  walls,
  items,
  doors,
  windows,
  floorPolygon,
  wallThickness,
  selected,
  onClick,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}) {
  if (!walls || walls.length === 0) return null;
  const wt = wallThickness || 0;
  const interactive = !!(onClick || onPointerDown);
  const { width, height, tx, tz } = computeLayoutTransform({
    walls,
    items,
    floorPolygon,
    wallThickness: wt,
  });

  return (
    <div
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{
        cursor: interactive ? 'pointer' : 'default',
        border: selected ? '3px solid #6ec6ff' : '3px solid transparent',
        borderRadius: 8,
        padding: 4,
        background: selected ? 'rgba(110,198,255,0.08)' : 'transparent',
        touchAction: onPointerDown ? 'none' : undefined,
      }}
    >
      <svg
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        style={{ background: '#1a1a2e', borderRadius: 6 }}
      >
        {floorPolygon && (
          <polygon
            points={floorPolygon.map((p) => `${tx(p.x)},${tz(p.z)}`).join(' ')}
            fill="#1a1a2e"
            stroke="#334"
            strokeWidth={1}
          />
        )}

        {walls.map((w, i) => {
          if (!w.start || !w.end) return null;
          const dx = w.end.x - w.start.x;
          const dz = w.end.z - w.start.z;
          const len = Math.hypot(dx, dz);
          if (len === 0) return null;
          const nx = (-dz / len) * (wt / 2);
          const nz = (dx / len) * (wt / 2);
          const corners = [
            { x: w.start.x + nx, z: w.start.z + nz },
            { x: w.end.x + nx, z: w.end.z + nz },
            { x: w.end.x - nx, z: w.end.z - nz },
            { x: w.start.x - nx, z: w.start.z - nz },
          ];
          return (
            <polygon
              key={`wall-${i}`}
              points={corners.map((p) => `${tx(p.x)},${tz(p.z)}`).join(' ')}
              fill="#4a4a5a"
              stroke="#e2e8f0"
              strokeWidth={1}
            />
          );
        })}

        {/* Doors */}
        {(doors || []).map((d, i) => {
          if (!d.center) return null;
          const doorWm = d.width || d.dimensions?.x || 0.9;
          const doorWpx = doorWm * M_TO_PX;
          const rotY = d.rotation?.y || 0;
          const wdx = Math.cos(rotY);
          const wdz = -Math.sin(rotY);
          const fpCx = floorPolygon
            ? floorPolygon.reduce((s, p) => s + p.x, 0) / floorPolygon.length
            : 0;
          const fpCz = floorPolygon
            ? floorPolygon.reduce((s, p) => s + p.z, 0) / floorPolygon.length
            : 0;
          const perpX = -wdz;
          const perpZ = wdx;
          const dot = (fpCx - d.center.x) * perpX + (fpCz - d.center.z) * perpZ;
          const inSign = dot > 0 ? 1 : -1;
          const inX = perpX * inSign;
          const inZ = perpZ * inSign;
          const isLeft = d.category?.door?.doorType === 'leftInswing';
          const hs = isLeft ? -1 : 1;
          const hingeW = {
            x: d.center.x + (hs * wdx * doorWm) / 2,
            z: d.center.z + (hs * wdz * doorWm) / 2,
          };
          const farW = {
            x: d.center.x - (hs * wdx * doorWm) / 2,
            z: d.center.z - (hs * wdz * doorWm) / 2,
          };
          const openW = {
            x: hingeW.x + inX * doorWm,
            z: hingeW.z + inZ * doorWm,
          };
          const hpx = tx(hingeW.x),
            hpz = tz(hingeW.z);
          const fpx = tx(farW.x),
            fpz = tz(farW.z);
          const opx = tx(openW.x),
            opz = tz(openW.z);
          const cross = (fpx - hpx) * (opz - hpz) - (fpz - hpz) * (opx - hpx);
          return (
            <g key={`door-${i}`}>
              <line
                x1={hpx}
                y1={hpz}
                x2={fpx}
                y2={fpz}
                stroke="#1a1a2e"
                strokeWidth={wt * M_TO_PX + 2}
              />
              <line
                x1={hpx}
                y1={hpz}
                x2={opx}
                y2={opz}
                stroke="#ef4444"
                strokeWidth={2}
              />
              <path
                d={`M ${fpx} ${fpz} A ${doorWpx} ${doorWpx} 0 0 ${cross > 0 ? 1 : 0} ${opx} ${opz}`}
                fill="none"
                stroke="#ef4444"
                strokeWidth={1}
                strokeDasharray="3,3"
                opacity={0.6}
              />
              <circle cx={hpx} cy={hpz} r={3} fill="#ef4444" />
            </g>
          );
        })}

        {/* Windows */}
        {(windows || []).map((w, i) => {
          if (!w.center) return null;
          const winW = (w.width || w.dimensions?.x || 1.0) * M_TO_PX;
          const rotY = w.rotation?.y || 0;
          const wcx = tx(w.center.x);
          const wcz = tz(w.center.z);
          const dx = (Math.cos(rotY) * winW) / 2;
          const dz = (-Math.sin(rotY) * winW) / 2;
          return (
            <g key={`win-${i}`}>
              <line
                x1={wcx - dx}
                y1={wcz - dz}
                x2={wcx + dx}
                y2={wcz + dz}
                stroke="#1a1a2e"
                strokeWidth={wt * M_TO_PX + 2}
              />
              <line
                x1={wcx - dx}
                y1={wcz - dz}
                x2={wcx + dx}
                y2={wcz + dz}
                stroke="#38bdf8"
                strokeWidth={3}
              />
            </g>
          );
        })}

        {/* Items */}
        {(items || []).map((item, i) => {
          const corners = itemCorners(item);
          const cx = tx(item.center.x);
          const cz = tz(item.center.z);
          const pointsStr = corners
            .map((p) => `${tx(p.x)},${tz(p.z)}`)
            .join(' ');

          // Reserved access zone in front of a built-in — drawn as a faint
          // amber, dashed strip so the empty space in the layout reads as
          // intentional (furniture is kept out of it).
          if (item.isClearance) {
            return (
              <g key={`item-${i}`}>
                <polygon
                  points={pointsStr}
                  fill="#f59e0b"
                  fillOpacity={0.12}
                  stroke="#f59e0b"
                  strokeOpacity={0.6}
                  strokeWidth={1}
                  strokeDasharray="3,3"
                />
                <text
                  x={cx}
                  y={cz}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#f59e0b"
                  fontSize={7}
                  fontFamily="monospace"
                  opacity={0.85}
                >
                  clearance
                </text>
              </g>
            );
          }

          const isKeepout =
            item.isKeepout || item.type === BUILTIN_KEEPOUT_TYPE;
          const color = isKeepout ? '#ef4444' : getColor(item.type);
          const label = isKeepout
            ? item.selected
              ? 'built-in ✎'
              : 'built-in'
            : (item.type || '').replace('dorm-', '');
          return (
            <g key={`item-${i}`}>
              <polygon
                points={pointsStr}
                fill={color}
                fillOpacity={isKeepout ? 0.35 : 0.6}
                stroke={color}
                strokeWidth={isKeepout ? (item.selected ? 3 : 2) : 1.5}
                strokeDasharray={isKeepout ? '4,3' : undefined}
              />
              <text
                x={cx}
                y={cz}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#fff"
                fontSize={8}
                fontFamily="monospace"
              >
                {label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Built-in (keep-out) geometry
// ---------------------------------------------------------------------------

// Centroid of the floor polygon ({x,z} points), used to decide which
// perpendicular of a wall points *into* the room.
function floorCentroid(floorPolygon) {
  const n = floorPolygon.length;
  const sx = floorPolygon.reduce((s, p) => s + p.x, 0);
  const sz = floorPolygon.reduce((s, p) => s + p.z, 0);
  return { x: sx / n, z: sz / n };
}

// Project world point `pt` onto wall segment; return { point, dist, t } where
// `point` is the closest point on the segment and `t` its clamped parameter.
function projectOntoWall(pt, wall) {
  const ax = wall.start.x,
    az = wall.start.z;
  const bx = wall.end.x,
    bz = wall.end.z;
  const dx = bx - ax,
    dz = bz - az;
  const lenSq = dx * dx + dz * dz;
  const t = lenSq === 0 ? 0 : ((pt.x - ax) * dx + (pt.z - az) * dz) / lenSq;
  const tc = Math.max(0, Math.min(1, t));
  const point = { x: ax + dx * tc, z: az + dz * tc };
  return {
    point,
    dist: Math.hypot(pt.x - point.x, pt.z - point.z),
    t: tc,
  };
}

// Unit inward normal for a wall (the perpendicular pointing toward the floor
// centroid). Uses the floor centroid so it's correct regardless of wall winding.
function wallInwardNormal(wall, centroid) {
  const dx = wall.end.x - wall.start.x;
  const dz = wall.end.z - wall.start.z;
  const len = Math.hypot(dx, dz) || 1;
  // Both perpendiculars to the wall direction.
  let nx = -dz / len;
  let nz = dx / len;
  const midX = (wall.start.x + wall.end.x) / 2;
  const midZ = (wall.start.z + wall.end.z) / 2;
  // Flip so the normal points from the wall toward the room interior.
  if ((centroid.x - midX) * nx + (centroid.z - midZ) * nz < 0) {
    nx = -nx;
    nz = -nz;
  }
  return { x: nx, z: nz };
}

// Given a world click and the room's walls, snap to the nearest wall and build
// a built-in descriptor whose center sits d/2 inside that wall, flush to it.
function makeBuiltInAtPoint(worldPt, walls, floorPolygon, dimsM) {
  const centroid = floorCentroid(floorPolygon);
  let best = null;
  walls.forEach((wall, wallIdx) => {
    if (!wall.start || !wall.end) return;
    const proj = projectOntoWall(worldPt, wall);
    if (!best || proj.dist < best.proj.dist) best = { wall, wallIdx, proj };
  });
  if (!best) return null;
  const inward = wallInwardNormal(best.wall, centroid);
  const offset = WALL_INNER_INSET + dimsM.d / 2;
  const center = {
    x: best.proj.point.x + inward.x * offset,
    z: best.proj.point.z + inward.z * offset,
  };
  return {
    id: `builtin-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
    center,
    dims: { ...dimsM },
    rotationY: facingRotation(inward),
    wallIdx: best.wallIdx,
  };
}

// Move an existing built-in to follow a drag: re-snap to the nearest wall at
// worldPt, preserving the box's id + dims.
function snapBuiltInTo(builtIn, worldPt, walls, floorPolygon) {
  const snapped = makeBuiltInAtPoint(
    worldPt,
    walls,
    floorPolygon,
    builtIn.dims
  );
  if (!snapped) return builtIn;
  return {
    ...builtIn,
    center: snapped.center,
    wallIdx: snapped.wallIdx,
    rotationY: snapped.rotationY,
  };
}

// Recompute a built-in's center so it stays flush to its wall after a depth
// edit. Pushes the wall-projected base point inward by the new d/2.
function reflushBuiltIn(builtIn, walls, floorPolygon) {
  const wall = walls[builtIn.wallIdx];
  if (!wall || !wall.start || !wall.end) return builtIn;
  const centroid = floorCentroid(floorPolygon);
  const inward = wallInwardNormal(wall, centroid);
  // Wall base = the built-in's center projected back onto its wall line. Deriving
  // it from the wall (not the current depth) keeps the flush edge fixed no matter
  // how depth changed, so the box grows inward from the wall rather than
  // straddling it.
  const base = projectOntoWall(builtIn.center, wall).point;
  const offset = WALL_INNER_INSET + builtIn.dims.d / 2;
  return {
    ...builtIn,
    center: {
      x: base.x + inward.x * offset,
      z: base.z + inward.z * offset,
    },
  };
}

// Map a built-in descriptor to the `items` shape LayoutSVG renders.
function builtInToItem(b, selected) {
  return {
    type: BUILTIN_KEEPOUT_TYPE,
    isKeepout: true,
    selected,
    center: { x: b.center.x, z: b.center.z },
    dimensions: { x: b.dims.w, y: b.dims.h, z: b.dims.d },
    rotation: { y: b.rotationY },
  };
}

// The reserved access strip in front of a built-in, as an `items`-shaped entry
// (flagged isClearance so LayoutSVG draws it faintly). Returns null if the
// built-in has no resolvable wall. Mirrors the optimizer's front clearance.
function builtInClearanceItem(b, walls, floorPolygon) {
  const wall = walls?.[b.wallIdx];
  if (!wall || !wall.start || !wall.end || !floorPolygon) return null;
  const inward = wallInwardNormal(wall, floorCentroid(floorPolygon));
  const dist = b.dims.d / 2 + BUILTIN_CLEARANCE_M / 2;
  return {
    type: BUILTIN_KEEPOUT_TYPE,
    isClearance: true,
    center: {
      x: b.center.x + inward.x * dist,
      z: b.center.z + inward.z * dist,
    },
    dimensions: { x: b.dims.w, y: 0, z: BUILTIN_CLEARANCE_M },
    rotation: { y: b.rotationY },
  };
}

// Built-in + its clearance strip, for a LayoutSVG `items` list. Clearance first
// so the solid box draws on top of it.
function builtInDisplayItems(b, walls, floorPolygon, selected) {
  const clearance = builtInClearanceItem(b, walls, floorPolygon);
  const box = builtInToItem(b, selected);
  return clearance ? [clearance, box] : [box];
}

// A point at parameter t (0..1) along a wall segment.
function pointAlongWall(wall, t) {
  return {
    x: wall.start.x + (wall.end.x - wall.start.x) * t,
    z: wall.start.z + (wall.end.z - wall.start.z) * t,
  };
}

// Synthesize the door + windows for a wizard dorm, mirroring the server's
// buildRoomForPipeline (a centered south-wall door + evenly spaced north-wall
// windows). Lets the Built-ins step render openings so the user can avoid them.
function synthesizeDormOpenings(walls) {
  if (!walls || walls.length < 4) return { doors: [], windows: [] };
  const doorWall = walls[DORM_DOOR_WALL_IDX];
  const doors = doorWall
    ? [
        {
          id: 'synth-door',
          center: pointAlongWall(doorWall, 0.5),
          width: DORM_DOOR_WIDTH_M,
          rotation: { y: doorWall.rotation?.y || 0 },
          category: { door: { doorType: 'leftInswing' } },
        },
      ]
    : [];
  const winWall = walls[DORM_WINDOW_WALL_IDX];
  const windows = [];
  if (winWall) {
    const wallLen = winWall.dimensions?.x || 3.0;
    const winWidth = Math.min(1.2, (wallLen / (DORM_WINDOW_COUNT + 1)) * 0.8);
    for (let i = 0; i < DORM_WINDOW_COUNT; i++) {
      const t = (i + 1) / (DORM_WINDOW_COUNT + 1);
      windows.push({
        id: `synth-window-${i}`,
        center: pointAlongWall(winWall, t),
        width: winWidth,
        rotation: { y: winWall.rotation?.y || 0 },
        category: { window: {} },
      });
    }
  }
  return { doors, windows };
}

// Build the persisted keep-out object for a built-in. Shared by room creation
// and handleApply so the two write paths cannot drift.
function buildKeepoutObject(b, walls) {
  return {
    id: `dorm-keepout-${newUuid()}`,
    type: BUILTIN_KEEPOUT_TYPE,
    category: { [BUILTIN_KEEPOUT_TYPE]: {} },
    isFixed: true,
    center: { x: b.center.x, y: b.dims.h / 2, z: b.center.z },
    rotation: { x: 0, y: b.rotationY, z: 0 },
    dimensions: { x: b.dims.w, y: b.dims.h, z: b.dims.d },
    story: 0,
    parentWallIds: [walls[b.wallIdx]?.id].filter(Boolean),
    attributes: { label: 'Built-in' },
  };
}

// ---------------------------------------------------------------------------
// Wizard steps
// ---------------------------------------------------------------------------

const HEIGHT_LABELS = {
  standard: 'Standard',
  captain: 'Captain',
  loft: 'Loft',
};
const BED_HEIGHTS = ['standard', 'captain', 'loft'];

// Field styles shared by the wizard's form steps and by `GoalPicker`, which is
// lifted out of the Room step so another dorm flow can show
// the SAME goal control without duplicating the markup or GOAL_DESCRIPTIONS.
const FIELD_INPUT_STYLE = {
  padding: '6px 10px',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 14,
  width: 70,
};
const FIELD_SELECT_STYLE = { ...FIELD_INPUT_STYLE, width: 'auto' };
const FIELD_ROW_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  marginBottom: 12,
};
const FIELD_LABEL_STYLE = { minWidth: 100, fontWeight: 500, fontSize: 14 };

/**
 * "Optimize for" picker — the goal select plus its description line.
 *
 * The goal is the one Room-step input that a re-layout of an EXISTING dorm room
 * still needs (dimensions and occupant count are already on the room), so it
 * lives on its own here and both callers render it.
 */
export function GoalPicker({ goal, setGoal, style }) {
  return (
    <div
      style={{
        ...FIELD_ROW_STYLE,
        alignItems: 'flex-start',
        marginBottom: 0,
        ...style,
      }}
    >
      <span style={FIELD_LABEL_STYLE}>Optimize for</span>
      <div>
        <select
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          style={FIELD_SELECT_STYLE}
        >
          <option value="">Balanced (default)</option>
          <option value="sleep">Sleep</option>
          <option value="study-focus">Study Focus</option>
          <option value="open-floor">Open Floor</option>
          <option value="workout">Workout</option>
          <option value="max-storage">Max Storage</option>
          <option value="natural-light">Natural Light</option>
          <option value="gaming">Gaming / Tech Hub</option>
          <option value="privacy">Privacy from Roommate</option>
        </select>
        <div
          style={{ fontSize: 12, color: '#666', marginTop: 4, maxWidth: 340 }}
        >
          {GOAL_DESCRIPTIONS[goal]}
        </div>
      </div>
    </div>
  );
}

function StepRoomConfig({
  widthFt,
  setWidthFt,
  lengthFt,
  setLengthFt,
  occupants,
  setOccupants,
  goal,
  setGoal,
}) {
  const inputStyle = FIELD_INPUT_STYLE;
  const selectStyle = FIELD_SELECT_STYLE;
  const rowStyle = FIELD_ROW_STYLE;
  const labelStyle = FIELD_LABEL_STYLE;

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={rowStyle}>
        <span style={labelStyle}>Width (ft)</span>
        <input
          type="number"
          value={widthFt}
          onChange={(e) => setWidthFt(Number(e.target.value))}
          style={inputStyle}
          min={8}
          max={30}
        />
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Length (ft)</span>
        <input
          type="number"
          value={lengthFt}
          onChange={(e) => setLengthFt(Number(e.target.value))}
          style={inputStyle}
          min={8}
          max={30}
        />
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Occupants</span>
        <select
          value={occupants}
          onChange={(e) => setOccupants(Number(e.target.value))}
          style={selectStyle}
        >
          {/* Bed style is per person (#2319), so a triple's third bed is its
              own. At three the Beds step requires the bunk — no arrangement
              table seats three separate beds (#2386). */}
          {[1, 2, 3].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
      <GoalPicker goal={goal} setGoal={setGoal} />
    </div>
  );
}

function StepMarkBuiltins({
  walls,
  floorPolygon,
  builtIns,
  setBuiltIns,
  selectedBuiltInId,
  setSelectedBuiltInId,
}) {
  const dimsM = {
    w: DEFAULT_BUILTIN_INCHES.w * IN_TO_M,
    d: DEFAULT_BUILTIN_INCHES.d * IN_TO_M,
    h: DEFAULT_BUILTIN_INCHES.h * IN_TO_M,
  };

  // Same transform LayoutSVG draws with, so we can invert a click exactly.
  const items = builtIns.flatMap((b) =>
    builtInDisplayItems(b, walls, floorPolygon, b.id === selectedBuiltInId)
  );
  // Preview the door + windows so the user can place built-ins clear of them.
  const openings = synthesizeDormOpenings(walls);
  const { minX, minZ } = computeLayoutTransform({
    walls,
    items,
    floorPolygon,
    wallThickness: WALL_THICKNESS,
  });

  // id of the built-in currently being dragged (null when not dragging).
  const dragId = useRef(null);

  // Convert a pointer event to a world point. Uses the <svg>'s own bounding box
  // (the wrapper has padding: 4) and inverts the exact transform used to draw it.
  const eventToWorld = (evt) => {
    const svgEl = evt.currentTarget.querySelector('svg');
    if (!svgEl) return null;
    const rect = svgEl.getBoundingClientRect();
    // viewBox px per screen px (svg width is 100%, viewBox is intrinsic px).
    const scaleX = svgEl.viewBox.baseVal.width / rect.width;
    const scaleZ = svgEl.viewBox.baseVal.height / rect.height;
    const px = (evt.clientX - rect.left) * scaleX;
    const pz = (evt.clientY - rect.top) * scaleZ;
    return {
      x: (px - PADDING) / M_TO_PX + minX,
      z: (pz - PADDING) / M_TO_PX + minZ,
    };
  };

  const handlePointerDown = (evt) => {
    const worldPt = eventToWorld(evt);
    if (!worldPt) return;
    // Grab the topmost existing built-in under the cursor → select + drag it.
    for (let i = builtIns.length - 1; i >= 0; i--) {
      const b = builtIns[i];
      if (pointInPolygon(worldPt, itemCorners(builtInToItem(b, false)))) {
        dragId.current = b.id;
        setSelectedBuiltInId(b.id);
        evt.currentTarget.setPointerCapture?.(evt.pointerId);
        return;
      }
    }
    // Empty space → place a new snapped box and start dragging it, so a single
    // press-and-drag both adds and positions it.
    const nb = makeBuiltInAtPoint(worldPt, walls, floorPolygon, dimsM);
    if (nb) {
      setBuiltIns((prev) => [...prev, nb]);
      setSelectedBuiltInId(nb.id);
      dragId.current = nb.id;
      evt.currentTarget.setPointerCapture?.(evt.pointerId);
    }
  };

  const handlePointerMove = (evt) => {
    if (dragId.current == null) return;
    const worldPt = eventToWorld(evt);
    if (!worldPt) return;
    setBuiltIns((prev) =>
      prev.map((b) =>
        b.id === dragId.current
          ? snapBuiltInTo(b, worldPt, walls, floorPolygon)
          : b
      )
    );
  };

  const handlePointerUp = (evt) => {
    if (dragId.current == null) return;
    evt.currentTarget.releasePointerCapture?.(evt.pointerId);
    dragId.current = null;
  };

  const selected = builtIns.find((b) => b.id === selectedBuiltInId);

  const applyInches = (b, field, inches) => {
    const nextM = Math.max(IN_TO_M * 6, inches * IN_TO_M);
    const updated = { ...b, dims: { ...b.dims, [field]: nextM } };
    // Depth changes must re-flush the box to its wall.
    return field === 'd'
      ? reflushBuiltIn(updated, walls, floorPolygon)
      : updated;
  };

  const updateSelectedInches = (field, deltaIn) => {
    setBuiltIns((prev) =>
      prev.map((b) =>
        b.id === selectedBuiltInId
          ? applyInches(b, field, Math.round(b.dims[field] / IN_TO_M) + deltaIn)
          : b
      )
    );
  };

  // Set an exact measurement typed by the user (commit on Enter/blur). Invalid
  // input falls back to the current value via re-render.
  const setSelectedInches = (field, raw) => {
    const inches = parseInt(raw, 10);
    if (!Number.isFinite(inches)) {
      // Force a re-render so the input snaps back to the stored value.
      setBuiltIns((prev) => [...prev]);
      return;
    }
    setBuiltIns((prev) =>
      prev.map((b) =>
        b.id === selectedBuiltInId ? applyInches(b, field, inches) : b
      )
    );
  };

  const deleteSelected = () => {
    setBuiltIns((prev) => prev.filter((b) => b.id !== selectedBuiltInId));
    setSelectedBuiltInId(null);
  };

  const stepBtn = {
    width: 26,
    height: 26,
    border: '1px solid #ccc',
    borderRadius: 4,
    cursor: 'pointer',
    background: '#f8f8f8',
    fontSize: 15,
  };

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ fontSize: 13, color: '#555', marginBottom: 8 }}>
        Tap a wall to add a built-in closet/cabinet — drag it to reposition.
        Furniture will avoid it.
      </div>
      <LayoutSVG
        walls={walls}
        items={items}
        doors={openings.doors}
        windows={openings.windows}
        floorPolygon={floorPolygon}
        wallThickness={WALL_THICKNESS}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />
      {selected && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: '1px solid #eee',
            borderRadius: 8,
            background: '#fafafa',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            Built-in size (inches)
          </div>
          {[
            ['w', 'Width (along wall)'],
            ['d', 'Depth (into room)'],
          ].map(([field, label]) => (
            <div
              key={field}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
              }}
            >
              <span style={{ minWidth: 150, fontSize: 13 }}>{label}</span>
              <button
                style={stepBtn}
                onClick={() => updateSelectedInches(field, -6)}
              >
                -
              </button>
              <input
                key={`${selected.id}-${field}-${Math.round(selected.dims[field] / IN_TO_M)}`}
                type="number"
                min={6}
                step={1}
                defaultValue={Math.round(selected.dims[field] / IN_TO_M)}
                aria-label={`${label} in inches`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
                onBlur={(e) => setSelectedInches(field, e.target.value)}
                style={{
                  width: 52,
                  textAlign: 'center',
                  fontSize: 14,
                  padding: '3px 4px',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                }}
              />
              <span style={{ fontSize: 13, color: '#888' }}>in</span>
              <button
                style={stepBtn}
                onClick={() => updateSelectedInches(field, 6)}
              >
                +
              </button>
            </div>
          ))}
          <Button variant="secondary" onClick={deleteSelected}>
            Delete built-in
          </Button>
        </div>
      )}
      {builtIns.length === 0 && (
        <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
          Optional — skip if there are no immovable closets or cabinets.
        </div>
      )}
    </div>
  );
}

// Point-in-polygon (ray cast) over {x,z} corners — used to hit-test built-ins.
function pointInPolygon(pt, corners) {
  let inside = false;
  for (let i = 0, j = corners.length - 1; i < corners.length; j = i++) {
    const xi = corners[i].x,
      zi = corners[i].z;
    const xj = corners[j].x,
      zj = corners[j].z;
    const iAbove = zi > pt.z;
    const jAbove = zj > pt.z;
    const straddlesZ = iAbove !== jAbove;
    const crossesX = pt.x < ((xj - xi) * (pt.z - zi)) / (zj - zi) + xi;
    if (straddlesZ && crossesX) inside = !inside;
  }
  return inside;
}

export function StepBeds({
  occupants,
  bedConfigs,
  setBedConfigs,
  bedSize,
  setBedSize,
  bunkMode,
  setBunkMode,
}) {
  const chipStyle = (active) => ({
    padding: '6px 14px',
    borderRadius: 16,
    fontSize: 13,
    fontWeight: 500,
    border: active ? '2px solid #6ec6ff' : '2px solid #ccc',
    background: active ? 'rgba(110,198,255,0.15)' : '#f8f8f8',
    color: active ? '#1a6fa0' : '#555',
    cursor: 'pointer',
  });
  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 12,
  };
  const labelStyle = { minWidth: 100, fontWeight: 500, fontSize: 14 };

  const setPersonHeight = (idx, height) => {
    setBedConfigs((prev) =>
      prev.map((cfg, i) => (i === idx ? { ...cfg, height } : cfg))
    );
  };

  // DERIVED, never written into state: forcing `bunkMode` true at three left a
  // double silently bunked after 3 -> 2, with nobody having asked for it.
  // `bunkMode` stays the student's own answer; this is what the room needs.
  const bunkRequired = bunkRequiredFor(occupants);
  const bunkActive = bunkActiveFor(occupants, bunkMode);
  const bunkPairCount = bunkActive ? Math.floor(occupants / 2) : 0;
  const soloStart = bunkActive ? bunkPairCount * 2 : 0;

  // A TRIPLE IS ONLY REPRESENTABLE AS A BUNK PLUS ONE MORE BED.
  // `TRIPLE_BED_CONFIGURATIONS` is the only arrangement table with room for a
  // third occupant and every entry in it is a bunk plus one bed; three
  // separate beds falls through to the TWO-bed table, where P3 appears in no
  // config, so their bed is dropped before placement and not even reported
  // unplaced. Measured in a 30x30 room: two of three beds. Not a fit problem.
  // Missing table tracked as #2386.

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={rowStyle}>
        <span style={labelStyle}>Bed Size</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            ['twin', 'Twin'],
            ['xl', 'Twin XL'],
          ].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setBedSize(val)}
              style={chipStyle(bedSize === val)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {occupants >= 2 && (
        <div style={{ ...rowStyle, marginBottom: 16 }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              cursor: bunkRequired ? 'default' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={bunkActive}
              disabled={bunkRequired}
              onChange={(e) => setBunkMode(e.target.checked)}
            />
            <span style={{ fontWeight: 500 }}>
              Bunk bed{occupants >= 4 ? 's' : ''}
            </span>
            <span style={{ color: '#888', fontSize: 12 }}>
              ({occupants >= 4 ? 'P1+P2 and P3+P4 share' : 'P1+P2 share'})
            </span>
            {bunkRequired && (
              <span style={{ color: '#888', fontSize: 12 }}>
                — required for {occupants}
              </span>
            )}
          </label>
        </div>
      )}

      <div style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
        {bunkActive &&
          Array.from({ length: bunkPairCount }, (_, bi) => (
            <div
              key={`bunk-${bi}`}
              style={{
                ...rowStyle,
                background: '#f0f7ff',
                borderRadius: 8,
                padding: '8px 12px',
              }}
            >
              <span style={{ ...labelStyle, color: '#1a6fa0' }}>
                Bunk (P{bi * 2 + 1}+P{bi * 2 + 2})
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                {BED_HEIGHTS.map((h) => (
                  <button
                    key={h}
                    onClick={() => {
                      setBedConfigs((prev) =>
                        prev.map((cfg, i) =>
                          i === bi * 2 || i === bi * 2 + 1
                            ? { ...cfg, height: h }
                            : cfg
                        )
                      );
                    }}
                    style={chipStyle(bedConfigs[bi * 2]?.height === h)}
                  >
                    {HEIGHT_LABELS[h]}
                  </button>
                ))}
              </div>
            </div>
          ))}

        {Array.from({ length: occupants - soloStart }, (_, i) => {
          const idx = soloStart + i;
          return (
            <div key={`p-${idx}`} style={rowStyle}>
              <span style={labelStyle}>P{idx + 1}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {BED_HEIGHTS.map((h) => (
                  <button
                    key={h}
                    onClick={() => setPersonHeight(idx, h)}
                    style={chipStyle(bedConfigs[idx]?.height === h)}
                  >
                    {HEIGHT_LABELS[h]}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepItemSelection({ selections, setSelections, occupants }) {
  const toggle = (key) => {
    setSelections((prev) => {
      const cur = prev[key];
      const enabled = !cur.enabled;
      return {
        ...prev,
        [key]: { enabled, quantity: enabled ? cur.quantity || 1 : 0 },
      };
    });
  };
  const setQty = (key, qty) => {
    setSelections((prev) => ({
      ...prev,
      [key]: { ...prev[key], quantity: Math.max(0, qty) },
    }));
  };

  const itemRow = (item, isDormProvided) => {
    const sel = selections[item.key] || { enabled: false, quantity: 0 };
    return (
      <div
        key={item.key}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '6px 0',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <input
          type="checkbox"
          checked={sel.enabled}
          onChange={() => toggle(item.key)}
        />
        <span style={{ flex: 1, fontSize: 14 }}>{item.label}</span>
        {sel.enabled && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              onClick={() => setQty(item.key, sel.quantity - 1)}
              style={{
                width: 24,
                height: 24,
                border: '1px solid #ccc',
                borderRadius: 4,
                cursor: 'pointer',
                background: '#f8f8f8',
              }}
            >
              -
            </button>
            <span style={{ minWidth: 20, textAlign: 'center', fontSize: 14 }}>
              {sel.quantity}
            </span>
            <button
              onClick={() => setQty(item.key, sel.quantity + 1)}
              style={{
                width: 24,
                height: 24,
                border: '1px solid #ccc',
                borderRadius: 4,
                cursor: 'pointer',
                background: '#f8f8f8',
              }}
            >
              +
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '8px 0', maxHeight: 400, overflowY: 'auto' }}>
      <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#555' }}>
        Dorm-Provided Items
      </h4>
      {DORM_PROVIDED.map((item) => itemRow(item, true))}
      <h4 style={{ margin: '16px 0 8px', fontSize: 14, color: '#555' }}>
        Personal Items
      </h4>
      {PERSONAL_ITEMS.map((item) => itemRow(item, false))}
    </div>
  );
}

export function StepPickLayout({
  layouts,
  walls,
  doors,
  windows,
  floorPolygon,
  builtIns,
  extraItems,
  selectedIdx,
  setSelectedIdx,
  loading,
  error,
  serverObstacles,
}) {
  // Marked built-ins overlaid on every preview so the student sees furniture
  // routed around them. The optimizer excludes them from layout.items and
  // returns them separately; fall back to the locally-marked set only before a
  // response exists.
  //
  // `extraItems` is the same overlay for callers whose keep-outs are already
  // persisted room objects (the re-layout flow) rather than wizard-marked
  // built-ins. It applies only in that pre-response window: once the server has
  // answered, those same objects come back as `serverObstacles` (the optimizer
  // reads them off the room doc), so overlaying both would double-draw them.
  const keepoutItems = serverObstacles?.length
    ? keepoutDisplayItems(serverObstacles)
    : [
        ...(builtIns || []).flatMap((b) =>
          builtInDisplayItems(b, walls, floorPolygon, false)
        ),
        ...(extraItems || []),
      ];
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
        <div style={{ fontSize: 18, marginBottom: 8 }}>
          Generating layouts...
        </div>
        <div style={{ fontSize: 14 }}>This may take 10-30 seconds</div>
      </div>
    );
  }
  if (error) {
    return <div style={{ color: '#ef4444', padding: 16 }}>{error}</div>;
  }
  if (!layouts || layouts.length === 0) {
    return (
      <div style={{ padding: 16, color: '#666' }}>
        No layouts generated. Try adjusting items or room size.
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
        maxHeight: 500,
        overflowY: 'auto',
        padding: '8px 0',
      }}
    >
      {layouts.slice(0, 6).map((layout, i) => (
        <div key={i}>
          <div
            style={{
              fontSize: 11,
              fontFamily: 'monospace',
              color: '#888',
              marginBottom: 2,
            }}
          >
            #{i + 1} score={layout.score?.toFixed(1)}
          </div>
          {/* A layout that had to drop something must say so on the tile — the
              furniture is simply absent from the drawing otherwise. */}
          {layout.unplacedItems?.length > 0 && (
            <div
              style={{
                fontSize: 11,
                fontFamily: 'monospace',
                color: '#f59e0b',
                marginBottom: 2,
              }}
            >
              ⚠ {layout.items.length} of{' '}
              {layout.items.length + layout.unplacedItems.length} items placed
              {layout.unplacedItems.every((u) =>
                (u.reason || '').includes('built-in')
              )
                ? ' — blocked by a built-in'
                : ''}
            </div>
          )}
          {layout.builtInAccessBlocked && (
            <div
              style={{
                fontSize: 11,
                fontFamily: 'monospace',
                color: '#ef4444',
                marginBottom: 2,
              }}
            >
              ⚠ a bed blocks access to a built-in
            </div>
          )}
          <LayoutSVG
            walls={walls}
            items={[...keepoutItems, ...layout.items]}
            doors={doors}
            windows={windows}
            floorPolygon={floorPolygon}
            wallThickness={WALL_THICKNESS}
            selected={selectedIdx === i}
            onClick={() => setSelectedIdx(i)}
          />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main wizard
// ---------------------------------------------------------------------------

export function buildBedConfigs(count) {
  return Array.from({ length: count }, () => ({ height: 'loft' }));
}

// Wizard step order. Built-ins sits between Room and Beds.
const STEP_ROOM = 1;
const STEP_BUILTINS = 2;
const STEP_BEDS = 3;
const STEP_ITEMS = 4;
const STEP_PICK = 5;

export default function DormWizardModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP_ROOM);

  // Step: Room
  const [widthFt, setWidthFt] = useState(12);
  const [lengthFt, setLengthFt] = useState(14);
  const [occupants, setOccupants] = useState(2);
  const [goal, setGoal] = useState(''); // '' = balanced (default weights, goal omitted)

  // Step: Built-ins (immovable keep-outs)
  const [builtIns, setBuiltIns] = useState([]);
  const [selectedBuiltInId, setSelectedBuiltInId] = useState(null);

  // Step: Beds
  const [bedConfigs, setBedConfigs] = useState(() => buildBedConfigs(2));
  const [bedSize, setBedSize] = useState('xl');
  const [bunkMode, setBunkMode] = useState(false);

  // Step: Items
  const [selections, setSelections] = useState(() => buildInitialSelections(2));

  // The per-person bed rows and the per-person item quantities have to follow
  // the occupant count. They did not, so a third occupant had no `bedConfigs`
  // entry at all and the wire config came back one person short — the server
  // then filled the gap from the room-level `bedConfig`, i.e. P1's (#2319).
  const changeOccupants = useCallback((next) => {
    setOccupants(next);
    setBedConfigs((prev) =>
      Array.from({ length: next }, (_, i) => prev[i] || { height: 'loft' })
    );
    // One desk / dresser / wardrobe per person, same rule the initial state
    // uses. The Items step comes after this one, so nothing hand-typed is lost.
    setSelections((prev) => {
      const nextSel = { ...prev };
      for (const item of DORM_PROVIDED) {
        if (!item.perPerson) continue;
        const cur = nextSel[item.key];
        if (!cur?.enabled) continue;
        nextSel[item.key] = { ...cur, quantity: next };
      }
      return nextSel;
    });
  }, []);

  // Step: Pick Layout
  const [roomId, setRoomId] = useState(null);
  const [structureId, setStructureId] = useState(null);
  const [layouts, setLayouts] = useState([]);
  const [synthDoors, setSynthDoors] = useState([]);
  const [serverObstacles, setServerObstacles] = useState([]);
  const [synthWindows, setSynthWindows] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Room rectangle derived purely from width/length. Available at the Built-ins
  // step (before generation) and reused by generation + the layout preview, so
  // the marking geometry and the previewed geometry are guaranteed identical.
  // `displayWalls` are the structure walls (they carry start/end that LayoutSVG
  // and wall-snapping need); floorPolygon is the floor as {x,z}.
  const { displayWalls, floorPolygon } = useMemo(() => {
    const { roomData, structureData } = buildDormRoomData(widthFt, lengthFt);
    return {
      displayWalls: structureData.walls,
      floorPolygon: roomData.floors[0].polygon.map((p) => ({
        x: p.x,
        z: p.z,
      })),
    };
  }, [widthFt, lengthFt]);

  const resetWizard = useCallback(() => {
    setStep(STEP_ROOM);
    setWidthFt(12);
    setLengthFt(14);
    setOccupants(2);
    setBuiltIns([]);
    setSelectedBuiltInId(null);
    setBedConfigs(buildBedConfigs(2));
    setBedSize('xl');
    setBunkMode(false);
    setSelections(buildInitialSelections(2));
    setRoomId(null);
    setStructureId(null);
    setLayouts([]);
    setSynthDoors([]);
    setSynthWindows([]);
    setSelectedIdx(null);
    setLoading(false);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetWizard();
    onClose();
  }, [resetWizard, onClose]);

  const handleGenerateLayouts = useCallback(async () => {
    setError(null);
    setLoading(true);
    setStep(STEP_PICK);
    setLayouts([]);
    setSelectedIdx(null);

    try {
      const { roomData, structureData } = buildDormRoomData(widthFt, lengthFt);
      // Persist marked built-ins as immovable keep-out objects. The
      // optimizeDormLayout callable reads them from the room doc server-side and
      // routes furniture around them (it also excludes them from layout.items).
      roomData.objects = builtIns.map((b) =>
        buildKeepoutObject(b, structureData.walls)
      );

      const configSelections = {};
      // The same rule the Beds step draws with. Reading raw `bunkMode` here
      // would build a triple with three separate beds and silently lose P3's.
      const bunkActive = bunkActiveFor(occupants, bunkMode);
      const bunkPairCount = bunkActive ? Math.floor(occupants / 2) : 0;
      const soloStart = bunkActive ? bunkPairCount * 2 : 0;

      if (bunkActive) {
        for (let bi = 0; bi < bunkPairCount; bi++) {
          const height = bedConfigs[bi * 2]?.height || 'standard';
          const key = `dorm-bunk-${height}-${bedSize}`;
          configSelections[key] = {
            quantity: (configSelections[key]?.quantity || 0) + 1,
          };
        }
      }

      for (let i = soloStart; i < occupants; i++) {
        const height = bedConfigs[i]?.height || 'loft';
        const key = `dorm-bed-${height}-${bedSize}`;
        configSelections[key] = {
          quantity: (configSelections[key]?.quantity || 0) + 1,
        };
      }

      for (const [key, { enabled, quantity }] of Object.entries(selections)) {
        if (enabled && quantity > 0) {
          configSelections[key] = { quantity };
        }
      }

      // Style is PER PERSON: only the occupants who actually share a bunk
      // carry 'bunk'. A room-level style meant the odd occupant out of a
      // bunked triple was generated as a copy of P1 (#2319).
      const serializedBedConfigs = bedConfigs
        .slice(0, occupants)
        .map((cfg, i) => ({
          size: bedSize,
          height: cfg.height,
          style: i < soloStart ? 'bunk' : 'bed',
        }));

      const dormConfig = {
        occupantCount: occupants,
        bedConfig: serializedBedConfigs[0],
        bedConfigs: serializedBedConfigs,
        doorWall: 'south',
        windowWall: 'north',
        windowCount: 2,
        selections: configSelections,
        // Omit the goal key entirely for Balanced (default weights).
        ...(goal ? { goal } : {}),
      };

      // A uuid, not a timestamp: two students committing the wizard in the same
      // millisecond used to mint the same id, and the second `setDoc` became an
      // update to a structure they do not own — denied as "Missing or
      // insufficient permissions", with no room created.
      const newStructureId = `dorm-${newUuid()}`;
      roomData.structureId = newStructureId;
      roomData.dormConfig = dormConfig;

      // Ownership: the rules reject a create without `createdBy`, and resolve
      // every LATER write through structure.projectId -> project.ownerId — so a
      // dorm room with no project would be created and then be uneditable.
      structureData.projectId = await requireOwnerProjectId();
      structureData.roomIds = [];

      // Structure first: the room points at it, so this order never leaves a
      // room referencing a document that does not exist.
      await setDoc(
        doc(db, 'structures', newStructureId),
        withCreatedBy(structureData)
      );
      const roomRef = await addDoc(
        collection(db, 'rooms'),
        withCreatedBy(roomData)
      );
      const newRoomId = roomRef.id;
      await updateDoc(doc(db, 'structures', newStructureId), {
        roomIds: [newRoomId],
      });

      setRoomId(newRoomId);
      setStructureId(newStructureId);

      const callable = createCallable('optimizeDormLayout', {
        timeout: 120000,
      });
      const result = await callable({
        roomId: newRoomId,
        structureId: newStructureId,
        dormConfig,
      });
      const data = result.data;

      setLayouts(data.layouts || []);
      setSynthDoors(data.doors || []);
      setSynthWindows(data.windows || []);
      // Prefer the built-ins the optimizer actually routed around over the
      // wizard's local copies: the server's clearance polygon keeps the back
      // edge on the wall, which a locally-derived strip doesn't, and this also
      // covers built-ins that were marked before this wizard session.
      setServerObstacles(data.fixedObstacles || []);

      if (!data.layouts || data.layouts.length === 0) {
        setError(
          'No valid layouts generated. Try a larger room or fewer items.'
        );
      }
    } catch (err) {
      setError(`Failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [
    widthFt,
    lengthFt,
    occupants,
    builtIns,
    bedConfigs,
    bedSize,
    bunkMode,
    selections,
    goal,
  ]);

  const handleApply = useCallback(async () => {
    if (selectedIdx == null || !layouts[selectedIdx]) return;
    setLoading(true);
    try {
      const layout = layouts[selectedIdx];
      // Merge the immovable built-ins back in — layout.items excludes them (the
      // cloud function filters isFixed obstacles out), so writing only
      // layout.items would wipe the keep-outs.
      const keepouts = builtIns.map((b) => buildKeepoutObject(b, displayWalls));
      await updateDoc(doc(db, 'rooms', roomId), {
        objects: [...keepouts, ...layout.items],
        doors: synthDoors,
        windows: synthWindows,
      });
      await updateDoc(doc(db, 'structures', structureId), {
        doors: synthDoors,
        windows: synthWindows,
      });
      handleClose();
      navigate(`/structure/${structureId}`);
    } catch (err) {
      setError(`Failed to apply layout: ${err.message}`);
      setLoading(false);
    }
  }, [
    selectedIdx,
    layouts,
    roomId,
    structureId,
    builtIns,
    displayWalls,
    synthDoors,
    synthWindows,
    handleClose,
    navigate,
  ]);

  const stepTitles = {
    [STEP_ROOM]: 'Room',
    [STEP_BUILTINS]: 'Built-ins',
    [STEP_BEDS]: 'Beds',
    [STEP_ITEMS]: 'Items',
    [STEP_PICK]: 'Pick a Layout',
  };
  const stepSubtitles = {
    [STEP_ROOM]: 'Set room dimensions and occupant count',
    [STEP_BUILTINS]: "Mark closets or cabinets that can't move",
    [STEP_BEDS]: 'Configure bed height for each person',
    [STEP_ITEMS]: 'Choose furniture for the dorm room',
    [STEP_PICK]:
      layouts.length > 0
        ? `${layouts.length} layouts generated — click one to select`
        : '',
  };

  // Entering the Beds step, size the per-person bed-config array to occupants.
  const goToBeds = () => {
    setBedConfigs((prev) => {
      if (prev.length < occupants) {
        return [
          ...prev,
          ...Array.from({ length: occupants - prev.length }, () => ({
            height: 'loft',
          })),
        ];
      }
      return prev.slice(0, occupants);
    });
    if (occupants < 2) setBunkMode(false);
    setStep(STEP_BEDS);
  };

  const actions = (
    <>
      {step > STEP_ROOM && step < STEP_PICK && (
        <Button variant="secondary" onClick={() => setStep((s) => s - 1)}>
          Back
        </Button>
      )}
      {step === STEP_ROOM && (
        <Button onClick={() => setStep(STEP_BUILTINS)}>Next: Built-ins</Button>
      )}
      {step === STEP_BUILTINS && <Button onClick={goToBeds}>Next: Beds</Button>}
      {step === STEP_BEDS && (
        <Button
          onClick={() => {
            setSelections(buildInitialSelections(occupants));
            setStep(STEP_ITEMS);
          }}
        >
          Next: Items
        </Button>
      )}
      {step === STEP_ITEMS && (
        <Button onClick={handleGenerateLayouts}>Generate Layouts</Button>
      )}
      {step === STEP_PICK && !loading && (
        <>
          <Button variant="secondary" onClick={() => setStep(STEP_ITEMS)}>
            Back
          </Button>
          <Button onClick={handleApply} disabled={selectedIdx == null}>
            View in 3D
          </Button>
        </>
      )}
    </>
  );

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={stepTitles[step]}
      subtitle={stepSubtitles[step]}
      size="lg"
      actions={actions}
    >
      {step === STEP_ROOM && (
        <StepRoomConfig
          widthFt={widthFt}
          setWidthFt={setWidthFt}
          lengthFt={lengthFt}
          setLengthFt={setLengthFt}
          occupants={occupants}
          setOccupants={changeOccupants}
          goal={goal}
          setGoal={setGoal}
        />
      )}
      {step === STEP_BUILTINS && (
        <StepMarkBuiltins
          walls={displayWalls}
          floorPolygon={floorPolygon}
          builtIns={builtIns}
          setBuiltIns={setBuiltIns}
          selectedBuiltInId={selectedBuiltInId}
          setSelectedBuiltInId={setSelectedBuiltInId}
        />
      )}
      {step === STEP_BEDS && (
        <StepBeds
          occupants={occupants}
          bedConfigs={bedConfigs}
          setBedConfigs={setBedConfigs}
          bedSize={bedSize}
          setBedSize={setBedSize}
          bunkMode={bunkMode}
          setBunkMode={setBunkMode}
        />
      )}
      {step === STEP_ITEMS && (
        <StepItemSelection
          selections={selections}
          setSelections={setSelections}
          occupants={occupants}
        />
      )}
      {step === STEP_PICK && (
        <StepPickLayout
          layouts={layouts}
          walls={displayWalls}
          doors={synthDoors}
          windows={synthWindows}
          floorPolygon={floorPolygon}
          builtIns={builtIns}
          serverObstacles={serverObstacles}
          selectedIdx={selectedIdx}
          setSelectedIdx={setSelectedIdx}
          loading={loading}
          error={error}
        />
      )}
    </Modal>
  );
}
